import { workspaceHasFeature, type SubscriptionTier } from '@/modules/workspaces/workspace-features';

export type TermKey =
  | 'vision'
  | 'goal'
  | 'rock'
  | 'milestone'
  | 'metric'
  | 'scorecard'
  | 'issue'
  | 'task'
  | 'person'
  | 'seat'
  | 'idea'
  | 'win'
  | 'headline'
  | 'process'
  | 'weight';

export type TerminologyConfig = Partial<Record<TermKey, string>>;
export type TerminologyPluralConfig = Partial<Record<TermKey, string>>;

export const DEFAULT_TERMS: Record<TermKey, string> = {
  vision: 'Vision',
  goal: 'Goal',
  rock: 'Rock',
  milestone: 'Milestone',
  metric: 'Metric',
  scorecard: 'Scorecard',
  issue: 'Issue',
  task: 'To-Do',
  person: 'Person',
  seat: 'Seat',
  idea: 'Idea',
  win: 'Win',
  headline: 'Headline',
  process: 'Process',
  weight: 'Point',
};

export type TerminologyMap = Record<TermKey, string>;

export function buildTerminologyMap(config: TerminologyConfig | null): TerminologyMap {
  return { ...DEFAULT_TERMS, ...config } as TerminologyMap;
}

export interface PresetTerminology {
  terms: TerminologyConfig;
  plurals?: TerminologyPluralConfig;
}

export const PRESET_TERMINOLOGY: Record<string, PresetTerminology> = {
  eos: {
    terms: { vision: 'V/TO', metric: 'Measurable' },
    plurals: { vision: 'V/TOs' },
  },
  scaling_up: {
    terms: { metric: 'KPI', goal: 'Initiative' },
  },
};

export interface TerminologySettings {
  terminology: TerminologyConfig | null;
  terminologyPlurals: TerminologyPluralConfig | null;
  terminologyPreset: string | null;
}

export interface EffectiveTerminology {
  terminology: TerminologyConfig;
  terminologyPlurals: TerminologyPluralConfig;
}

export function renamedTermKeys(
  settings: Pick<TerminologySettings, 'terminology'> | null | undefined,
  tier: SubscriptionTier | null | undefined,
  isPersonal = false,
): Set<TermKey> {
  if (!settings?.terminology || !workspaceHasFeature('terminology', tier, isPersonal)) return new Set();
  return new Set((Object.keys(DEFAULT_TERMS) as TermKey[]).filter((key) => settings.terminology?.[key]));
}

export function effectiveTerminology(
  settings: TerminologySettings | null | undefined,
  tier: SubscriptionTier | null | undefined,
  isPersonal = false,
  presets: Record<string, PresetTerminology> = PRESET_TERMINOLOGY,
): EffectiveTerminology {
  const layers: { terms: TerminologyConfig; plurals: TerminologyPluralConfig }[] = [];
  if (settings && workspaceHasFeature('terminology', tier, isPersonal)) {
    layers.push({ terms: settings.terminology ?? {}, plurals: settings.terminologyPlurals ?? {} });
  }
  const preset = settings?.terminologyPreset ? presets[settings.terminologyPreset] : undefined;
  if (preset && workspaceHasFeature('presets', tier, isPersonal)) {
    layers.push({ terms: preset.terms, plurals: preset.plurals ?? {} });
  }

  const terminology: TerminologyConfig = {};
  const terminologyPlurals: TerminologyPluralConfig = {};
  for (const key of Object.keys(DEFAULT_TERMS) as TermKey[]) {
    const layer = layers.find((l) => l.terms[key]);
    if (!layer) continue;
    terminology[key] = layer.terms[key];
    if (layer.plurals[key]) terminologyPlurals[key] = layer.plurals[key];
  }
  return { terminology, terminologyPlurals };
}
